import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useCancelSubscription from '../../services/subscription/useCancelSubscription'
import { Select } from '../Select'
import Label from '../Formik/Label'
import RequiredLabelWrapper from '../UI/RequiredLabelWrapper'
import {
  CANCELLATION_REASON_KEYS,
  cancellationReasonI18nKey,
} from '../../langs/cancellationReasons'

interface Props {
  onClose: () => void
  subscriptionId: number
  isSubscriptionCancelled: boolean
}

/** The backend trims the comment and refuses anything longer with a 400, so
 *  the textarea stops at the same number rather than letting the customer
 *  write past it and lose the lot to a field error. */
const COMMENT_MAX_LENGTH = 2000

// `Select` runs its option labels through `t` itself, so these carry the
// translation key rather than the wording.
const reasonOptions = CANCELLATION_REASON_KEYS.map((reason) => ({
  value: reason,
  label: cancellationReasonI18nKey(reason),
}))

const ModalCancelSubscription = ({
  onClose,
  subscriptionId,
  isSubscriptionCancelled,
}: Props) => {
  const { t } = useTranslation()
  const { mutate, isLoading } = useCancelSubscription()
  const [reason, setReason] = useState('')
  const [comment, setComment] = useState('')

  // The endpoint is a toggle, and on the restore direction it validates the
  // body and then ignores it - asking someone why they are cancelling while
  // they are un-cancelling would be nonsense, so the picker only appears on
  // the way out.
  const isCancelling = !isSubscriptionCancelled

  // Optional to the backend, required by us: collecting the reason is the
  // whole point of the endpoint gaining a body.
  const isSubmitDisabled = isLoading || (isCancelling && !reason)

  const onConfirm = () =>
    mutate(
      isCancelling ? { subscriptionId, reason, comment } : { subscriptionId },
      { onSuccess: () => onClose() },
    )

  return (
    <div className='overflow-y-auto overflow-x-hidden fixed right-0 left-0 top-0 z-50 flex justify-center items-center md:inset-0 h-modal h-full bg-[rgba(0,0,0,0.1)]'>
      <div className='relative px-4 w-full  h-auto  max-w-2xl'>
        <div className='relative bg-white rounded-20px shadow dark:bg-mainBg'>
          <div className='flex justify-end p-2'>
            <button
              onClick={onClose}
              type='button'
              className='text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white transition-all'
            >
              <svg
                className='w-5 h-5'
                fill='currentColor'
                viewBox='0 0 20 20'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fillRule='evenodd'
                  d='M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z'
                  clipRule='evenodd'
                ></path>
              </svg>
            </button>
          </div>

          <div className='p-6 pt-0 text-center'>
            <svg
              className='mx-auto mb-4 w-14 h-14 text-red-600 dark:text-red-600'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='2'
                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              ></path>
            </svg>

            <h3 className='mb-5 text-lg font-normal text-gray-600'>
              {t(
                isSubscriptionCancelled
                  ? 'reactivateSubscriptionDescription'
                  : 'cancelSubscriptionDescription',
              )}
            </h3>

            {isCancelling && (
              <div className='mb-6 text-left'>
                <div className='flex flex-col'>
                  <RequiredLabelWrapper>
                    <Label text={t('whyAreYouCancelling')} />
                  </RequiredLabelWrapper>
                  <Select
                    options={reasonOptions}
                    placeholder={t('selectAReason')}
                    value={reasonOptions.find(
                      (option) => option.value === reason,
                    )}
                    onChange={(option: { value: string; label: string }) =>
                      setReason(option.value)
                    }
                  />
                </div>

                <div className='flex flex-col mt-4'>
                  <Label text={t('anythingElseYouWouldLikeToTellUs')} />
                  <textarea
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    maxLength={COMMENT_MAX_LENGTH}
                    rows={3}
                    placeholder={t('textHere')}
                    className='resize-none bg-white rounded-lg px-4 py-3 w-full border-[1px] border-solid border-borderGray focus:border-primaryBlue hover:border-primaryBlue transition-all outline-none'
                  />
                  <p className='text-sm mt-1 ml-1 text-lightGray'>
                    {`${comment.trim().length}/${COMMENT_MAX_LENGTH} ${t(
                      'characterLimit',
                    )}`}
                  </p>
                </div>
              </div>
            )}

            <button
              disabled={isSubmitDisabled}
              onClick={onConfirm}
              type='button'
              className='gap-2 text-white font-medium rounded-lg text-base inline-flex items-center px-5 py-2.5 text-center mr-2 transition-all bg-red-600 hover:bg-red-800 focus:ring-4 focus:ring-red-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-600'
            >
              {t('yesIamSure')}
            </button>

            <button
              onClick={onClose}
              type='button'
              className='text-white bg-white hover:bg-gray-100 focus:ring-4 focus:ring-gray-300 rounded-lg border border-gray-200 text-base font-medium px-5 py-2.5 hover:text-gray-900 focus:z-10 dark:bg-gray-700 dark:border-gray-500 dark:hover:text-white transition-all dark:hover:bg-gray-600'
            >
              {t('noCancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ModalCancelSubscription
